import type { Clock } from "../clock.js";

/**
 * A calendar day as `YYYY-MM-DD`, and the rules for reading one safely.
 *
 * ==========================================================================================
 * WHY THIS IS IN THE SHARED KERNEL, AND WHY IT IS FUNCTIONS RATHER THAN A CLASS
 * ==========================================================================================
 *
 * Three contexts had written this logic before feature 015: savings' `AsOfDate`, properties'
 * `IsoDate`, and expenses' `IsoDate` — each with a header saying it was COPIED rather than imported,
 * because a domain package may not import another (Principle IV), and each naming promotion as the
 * alternative worth taking once another context needed it. Income is the fourth. This is that
 * promotion.
 *
 * It is deliberately NOT a fourth `IsoDate` class replacing the three. Each context's class returns
 * its OWN error — `ExpenseStartDateInvalidError`, `PROP_DATE_OUT_OF_RANGE`, `SAV_DATE_OUT_OF_RANGE` —
 * which reaches the HTTP mapper and the API client, and which is the taxonomy Principle V asks for: a
 * context says why its own value was refused. Unifying the class would flatten those into one code
 * and rewrite fifteen existing test assertions to no benefit.
 *
 * So what lives here is the part that can drift SILENTLY. A wrong error code fails loudly the first
 * time anyone looks. A date read through a local-time accessor shifts by one day only for processes
 * behind UTC, only near midnight, and changes which month a figure lands in — with nothing on screen
 * to indicate it. That asymmetry is the whole argument for which half is shared.
 *
 * ==========================================================================================
 * IT IS A STRING, AND THAT IS THE POINT
 * ==========================================================================================
 *
 * The generated Kysely types map `date` and `timestamptz` to the same alias, so the compiler will not
 * tell them apart, and the driver hands back a `Date` for each. Held as `YYYY-MM-DD` the value
 * compares exactly — ISO dates sort lexicographically — and has no timezone to get wrong. The one
 * place it becomes a `Date` is `isoDayToUtcDate`, built with `Date.UTC`.
 */

export const ISO_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** Absolute. A financial record dated before this is a typo, not a record. */
export const EARLIEST_ISO_DAY = "1900-01-01";

/**
 * Relative to today. Generous enough for a thirty-year mortgage maturity and a future-dated
 * valuation, tight enough to catch `1026` keyed for `2026`. The window catches a typo; it does not
 * express a product rule.
 */
export const DEFAULT_MAX_YEARS_AHEAD = 40;

/** `YYYY-MM-DD` for a Date, read in UTC. Never uses a local-time accessor. */
export function toIsoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** UTC midnight for a `YYYY-MM-DD`. The ONLY place one of these becomes a `Date`. */
export function isoDayToUtcDate(value: string): Date {
  const [year, month, day] = value.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(year, month - 1, day));
}

/**
 * Why a candidate day was refused, in a form each context can turn into its own error.
 *
 * `message` and `details` are carried here rather than composed at the call site so that three
 * contexts phrase the same refusal identically — the wording is part of what a member reads, and
 * three versions of "that is not a date" is the drift this module exists to stop.
 */
export interface IsoDayProblem {
  reason: "malformed" | "not-a-date" | "too-early" | "too-far";
  message: string;
  details?: Record<string, unknown>;
}

export interface IsoDayBounds {
  /** Defaults to `DEFAULT_MAX_YEARS_AHEAD`. Savings uses a tighter horizon. */
  maxYearsAhead?: number;
}

/**
 * `null` when the input is a usable calendar day, an `IsoDayProblem` when it is not.
 *
 * Returns a problem rather than a `Result` on purpose: the caller is a value object's `create`, which
 * has its own error type to construct and would otherwise have to unwrap one Result to build another.
 */
export function validateIsoDay(
  input: string,
  clock: Clock,
  bounds: IsoDayBounds = {},
): IsoDayProblem | null {
  if (!ISO_DAY_PATTERN.test(input)) {
    return {
      reason: "malformed",
      message: "Enter a date as YYYY-MM-DD.",
      // `reason` TRAVELS IN `details` TOO — feature 021. The message is composed here, in the
      // kernel, where no catalogue can be reached; the reason is the identifier the browser
      // translates from. Every caller already passes `details` through to its DomainError, so this
      // one line carries the distinction to every date refusal in the product.
      details: { reason: "malformed" },
    };
  }

  // Round-tripping catches what the pattern cannot: 2026-02-31 matches the shape and is not a day.
  // `Date.UTC` rolls it forward to 2026-03-03, which no longer equals the input.
  const parsed = isoDayToUtcDate(input);
  if (Number.isNaN(parsed.getTime()) || toIsoDay(parsed) !== input) {
    return {
      reason: "not-a-date",
      message: "That is not a date.",
      details: { reason: "not-a-date" },
    };
  }

  if (input < EARLIEST_ISO_DAY) {
    return {
      reason: "too-early",
      message: "That date is implausibly far in the past.",
      details: { reason: "too-early", earliest: EARLIEST_ISO_DAY },
    };
  }

  const maxYearsAhead = bounds.maxYearsAhead ?? DEFAULT_MAX_YEARS_AHEAD;
  const today = clock.now();
  const horizon = toIsoDay(
    new Date(
      Date.UTC(today.getUTCFullYear() + maxYearsAhead, today.getUTCMonth(), today.getUTCDate()),
    ),
  );
  if (input > horizon) {
    return {
      reason: "too-far",
      message: `That date is more than ${maxYearsAhead} years away.`,
      details: { reason: "too-far", horizon, maxYearsAhead },
    };
  }

  return null;
}
